import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Camera,
  Film,
  Layers,
  ListChecks,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Timer,
  Wallet,
} from "lucide-react";
import { PLAN_DISCLAIMER, planAtLeast } from "@/lib/plans";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { getMyPlanStatus } from "@/lib/plans.functions";
import { VideoScanner } from "@/components/scan/VideoScanner";
import { useLocale } from "@/lib/i18n/locale-context";
import { localizedPath } from "@/lib/i18n/slug-map";

const TITLE = "Videoskanning – filma hemmet och se vad prylarna är värda | Sellavo";
const DESCRIPTION =
  "Filma ett rum i en minut. Vi listar varje pryl vi hittar, uppskattar andrahandsvärdet och sparar allt i Mina prylar. Ingår i Sellavo Pro.";

export const Route = createFileRoute("/videoskanning")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://sellavo.com/videoskanning" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/videoskanning" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Så fungerar videoskanning på Sellavo",
          description: DESCRIPTION,
          step: [
            { "@type": "HowToStep", name: "Filma rummet i upp till 60 sekunder med mobilen" },
            { "@type": "HowToStep", name: "Filmen görs redo och skickas in" },
            { "@type": "HowToStep", name: "Vi listar prylarna och uppskattar värdet" },
            { "@type": "HowToStep", name: "Fota det du vill sälja och publicera annonsen" },
          ],
        }),
      },
    ],
  }),
  component: VideoScanInfoPage,
});

const STEPS = [
  {
    icon: Film,
    title: "1. Filma rummet",
    body: "Gå långsamt runt i rummet i upp till 60 sekunder. Panorera lugnt förbi soffan, hyllan, cykeln, allt du vill få värderat. Ljudet spelar ingen roll, vi tittar bara på bilden.",
  },
  {
    icon: Layers,
    title: "2. Vi gör filmen redo",
    body: "Filmen komprimeras innan den skickas, så att den går snabbt iväg utan att bli suddig. Du behöver inte göra något, det sköter sig självt medan du väntar.",
  },
  {
    icon: ScanSearch,
    title: "3. Vi går igenom filmen",
    body: "Vi plockar ut prylarna vi ser i rummet och sätter kategori, skick, antal och ett uppskattat prisintervall på var och en. Samma pryl räknas bara en gång.",
  },
  {
    icon: ListChecks,
    title: "4. Allt sparas i Mina prylar",
    body: "Skanningen sparas med en stillbild ur filmen på varje pryl. Du kan gå tillbaka när du vill, jämföra listor och plocka fram en pryl i taget när du känner för att sälja.",
  },
];

const FACTS = [
  {
    icon: Timer,
    title: "Klart medan du gör annat",
    body: "Skanningen fortsätter i bakgrunden när du surfar vidare på sajten. Du får en notis så fort listan är klar.",
  },
  {
    icon: Wallet,
    title: "Summan på hela rummet",
    body: "Du ser både värdet per pryl och totalen — bra inför flytt, dödsbo, försäkring eller storstädning.",
  },
  {
    icon: Camera,
    title: "Sälj i nästa steg",
    body: "Bilder ur en rumsfilm blir aldrig lika bra som en riktig produktbild. När du bestämt dig fotar du prylen separat — då skriver vi annonsen åt dig på några sekunder.",
  },
  {
    icon: ShieldCheck,
    title: "Priserna är vägledande",
    body: "Värderingarna är uppskattningar utifrån vad liknande begagnade saker brukar kosta. Du bestämmer alltid själv sluttexten och priset.",
  },
];

export function VideoScanInfoPage() {
  const { lang } = useLocale();
  const { user } = useAuth();
  const { data: planStatus } = useQuery({
    queryKey: ["plan-status"],
    queryFn: () => getMyPlanStatus(),
    enabled: !!user,
  });
  const isPro = planAtLeast(planStatus?.plan, "pro");

  if (lang !== "sv") return <TranslatedVideoScan lang={lang} isPro={isPro} user={!!user} />;

  if (user && isPro) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
        <header className="mb-6 max-w-2xl">
          <p className="text-xs font-semibold uppercase text-primary">Videoskanning</p>
          <h1 className="mt-2 font-heading text-3xl font-bold">Filma rummet. Spara varje pryl.</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Vi plockar först riktiga bilder ur filmen. När filmen och bilderna är uppladdade kan du
            lämna sidan medan resten blir klart i bakgrunden.
          </p>
        </header>
        <VideoScanner />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      <section className="relative overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-ocean-900 to-ocean-700 p-6 text-white shadow-sm sm:p-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-10 hidden h-[150%] w-72 -rotate-12 opacity-25 sm:block"
        >
          <div className="flex h-full w-full flex-col gap-2 rounded-md bg-white/10 p-2">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex flex-1 items-center gap-2">
                <span className="h-3 w-3 shrink-0 rounded-[2px] bg-white/30" />
                <span className="h-full flex-1 rounded-sm bg-white/15" />
                <span className="h-3 w-3 shrink-0 rounded-[2px] bg-white/30" />
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Ingår i Pro
          </span>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight sm:text-4xl">
            Filma hemmet — se vad prylarna är värda
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
            En minut film räcker. Vi går igenom rummet, listar sak för sak och sätter ett
            uppskattat andrahandsvärde på allt. Listan sparas i Mina prylar så att du kan sälja i
            din egen takt.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="secondary" className="gap-2 font-semibold">
              {isPro ? (
                <Link to="/videoskanning">
                  Skanna en film <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <Link to="/priser">
                  Skaffa Pro <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </Button>
            <Button
              asChild
              variant="ghost"
              className="gap-2 text-white hover:bg-white/15 hover:text-white"
            >
              <Link to="/tips">Tips för att sälja snabbare</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold">Så fungerar det</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {STEPS.map((step) => (
            <div key={step.title} className="rounded-xl border border-border bg-card p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <step.icon className="h-4.5 w-4.5" />
              </span>
              <h3 className="mt-3 font-semibold">{step.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold">Bra att veta</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {FACTS.map((fact) => (
            <div key={fact.title} className="rounded-xl border border-border bg-card p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <fact.icon className="h-4.5 w-4.5" />
              </span>
              <h3 className="mt-3 font-semibold">{fact.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{fact.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-xl border border-border bg-card p-5">
        <h2 className="font-heading text-2xl font-bold">Hellre foton? Fota i bulk</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Går det inte att filma kan du istället gå runt och fota. Ladda upp upp till{" "}
          <strong>30 bilder på en gång</strong> — ta gärna flera bilder på samma sak — så grupperar
          vi ihop bilderna per pryl, sätter namn och uppskattat värde. Du granskar grupperna, slår
          ihop eller delar upp dem, och sparar listan i Mina prylar. Fotoskanning ingår i{" "}
          <strong>Plus (3 omgångar per dygn)</strong> och <strong>Pro (15 per dygn)</strong>.
        </p>
        <Button asChild variant="outline" className="mt-4 gap-2">
          <Link to="/mina-prylar" search={{ lage: "foton" }}>
            Prova fotoskanning <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </section>

      <section className="mt-10 rounded-xl border border-border bg-muted/40 p-5">
        <h2 className="font-heading text-lg font-bold">Gränser och rimlig användning</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Videoskanning ingår i Pro inom rimlig användning: <strong>5 skanningar per timme</strong>{" "}
          och <strong>10 per dygn</strong>, filmer på upp till 60 sekunder. Bakgrundsrensning av
          bilder är begränsad till 3 per dygn på Gratis, 15 på Plus och 50 på Pro. Fotoskanning: 3
          omgångar per dygn på Plus och 15 på Pro, upp till 30 bilder per omgång. Fullständiga
          villkor hittar du i användarvillkoren, och priserna ser du på{" "}

          <Link to="/priser" className="underline underline-offset-2">
            prissidan
          </Link>
          .
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {PLAN_DISCLAIMER} Värderingarna utgör inte en värdering i juridisk eller
          försäkringsmässig mening.
        </p>
      </section>
    </main>
  );
}

function TranslatedVideoScan({ lang, isPro, user }: { lang: "en" | "es"; isPro: boolean; user: boolean }) {
  const en = lang === "en";
  if (user && isPro) return <main className="mx-auto max-w-5xl px-4 pb-16 pt-8 sm:px-6"><header className="mb-6 max-w-2xl"><p className="text-xs font-semibold uppercase text-primary">{en ? "Video scanning" : "Escaneo por vídeo"}</p><h1 className="mt-2 font-heading text-3xl font-bold">{en ? "Film the room. Save every item." : "Graba la habitación. Guarda cada objeto."}</h1><p className="mt-2 text-muted-foreground">{en ? "We extract clear frames first. You can leave the page after upload while the scan finishes in the background." : "Primero extraemos imágenes claras. Puedes salir de la página tras subir el vídeo mientras el análisis termina en segundo plano."}</p></header><VideoScanner /></main>;
  const steps = en ? [["1. Film the room", "Move slowly around the room for up to 60 seconds. Audio does not matter."], ["2. We prepare the video", "The video is compressed before upload so it transfers quickly without becoming blurry."], ["3. We review the video", "We identify each item once and suggest its category, condition, quantity and price range."], ["4. Everything is saved", "Each item is saved with a still image so you can return and decide what to sell later."]] : [["1. Graba la habitación", "Muévete despacio por la habitación durante un máximo de 60 segundos. El sonido no importa."], ["2. Preparamos el vídeo", "Comprimimos el vídeo antes de subirlo para enviarlo rápidamente sin perder claridad."], ["3. Revisamos el vídeo", "Identificamos cada objeto una vez y sugerimos categoría, estado, cantidad y rango de precio."], ["4. Guardamos todo", "Cada objeto se guarda con una imagen para que puedas volver y decidir qué vender más adelante."]];
  const facts = en ? [["Runs in the background", "Continue browsing and receive a notification when the list is ready."], ["See the room's total", "View the estimated value per item and for the whole room."], ["Sell in the next step", "Take separate product photos when you decide to sell, and we write the listing."], ["Prices are guidance", "Values are estimates. You always decide the final text and price."]] : [["Funciona en segundo plano", "Sigue navegando y recibe una notificación cuando la lista esté lista."], ["Consulta el valor total", "Ve el valor estimado de cada objeto y de toda la habitación."], ["Vende en el siguiente paso", "Haz fotos separadas cuando decidas vender y escribiremos el anuncio."], ["Los precios son orientativos", "Los valores son estimaciones. Tú decides siempre el texto y el precio final."]];
  const stepIcons=[Film,Layers,ScanSearch,ListChecks], factIcons=[Timer,Wallet,Camera,ShieldCheck];
  return <main className="mx-auto max-w-4xl px-4 pb-16 pt-8 sm:px-6">
    <section className="rounded-2xl border border-primary/40 bg-ocean-900 p-6 text-white sm:p-10"><span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold uppercase"><Sparkles className="h-3.5 w-3.5"/>{en ? "Included in Pro" : "Incluido en Pro"}</span><h1 className="mt-3 font-heading text-3xl font-bold">{en ? "Film your home — see what your things are worth" : "Graba tu casa y descubre cuánto valen tus cosas"}</h1><p className="mt-3 max-w-2xl text-white/85">{en ? "One minute of video is enough. We list each item, estimate its second-hand value and save everything so you can sell at your own pace." : "Un minuto de vídeo es suficiente. Enumeramos cada objeto, estimamos su valor de segunda mano y lo guardamos para que vendas a tu ritmo."}</p><Button asChild variant="secondary" className="mt-6"><Link to={localizedPath("/priser",lang)}>{en ? "Get Pro" : "Obtener Pro"}<ArrowRight className="h-4 w-4"/></Link></Button></section>
    <LocalizedGrid title={en ? "How it works" : "Cómo funciona"} data={steps} icons={stepIcons}/>
    <LocalizedGrid title={en ? "Good to know" : "Información útil"} data={facts} icons={factIcons}/>
    <section className="mt-10 rounded-xl border border-border bg-card p-5"><h2 className="font-heading text-2xl font-bold">{en ? "Prefer photos? Scan in bulk" : "¿Prefieres fotos? Escanea en lote"}</h2><p className="mt-2 text-sm text-muted-foreground">{en ? "Upload up to 30 photos at once. We group photos of the same item, suggest names and values, and save the list. Photo scanning is included in Plus and Pro." : "Sube hasta 30 fotos a la vez. Agrupamos las del mismo objeto, sugerimos nombres y valores y guardamos la lista. El escaneo de fotos está incluido en Plus y Pro."}</p></section>
  </main>;
}

function LocalizedGrid({ title, data, icons }: { title: string; data: string[][]; icons: typeof STEPS[number]["icon"][] }) {
  return <section className="mt-10"><h2 className="font-heading text-2xl font-bold">{title}</h2><div className="mt-5 grid gap-4 sm:grid-cols-2">{data.map(([heading,body],i)=>{const Icon=icons[i]; return <div key={heading} className="rounded-xl border border-border bg-card p-5"><Icon className="h-5 w-5 text-primary"/><h3 className="mt-3 font-semibold">{heading}</h3><p className="mt-1.5 text-sm text-muted-foreground">{body}</p></div>})}</div></section>;
}
